import { useRef, useState } from 'react';
import ActionLink from '../../../../Components/Dashboard/ActionLink';
import API from '../../../../Utils/api';
import useAsync from '../../../../Hooks/useAsync';
import Card from '../../../../Components/Dashboard/Card';
import { CardEmpty, CardError, CardLoading } from '../../../../Components/Dashboard/CardState';
import { formatDayMonth } from '../../../../Utils/dates/formatDate';
import useCoachRoutes from '../useCoachRoutes';
import styles from './GroupsProgramsCard.module.css';

const TABS = ['groups', 'programs'];
const LABEL = { groups: 'Groups', programs: 'Programs' };

const List = ({ which, coachId }) => {
    const { status, data, error, reload } = useAsync(
        () => (which === 'groups' ? API.getMyGroups(coachId) : API.getMyPrograms(coachId)).then(res => res.data),
        [which, coachId]
    );
    if (status === 'loading' && !data) return <CardLoading label={LABEL[which]} />;
    if (status === 'error') return <CardError label={LABEL[which]} status={error?.response?.status} onRetry={reload} />;
    if (!data || data.length === 0) return <CardEmpty>No {which} yet.</CardEmpty>;

    return (
        <ul className={styles.list}>
            {data.map(item =>
                which === 'groups' ? (
                    <li key={item._id}>
                        <div>
                            <div className={styles.name}>{item.name}</div>
                            {item.description && <div className={styles.sub}>{item.description}</div>}
                        </div>
                        <span className={styles.meta}>
                            {item.athleteCount} {item.athleteCount === 1 ? 'athlete' : 'athletes'}
                        </span>
                    </li>
                ) : (
                    <li key={item._id}>
                        <div>
                            <div className={styles.name}>{item.name}</div>
                            {item.type && <div className={styles.sub}>{item.type}</div>}
                        </div>
                        {item.updatedAt && <span className={styles.meta}>Updated {formatDayMonth(item.updatedAt)}</span>}
                    </li>
                )
            )}
        </ul>
    );
};

const GroupsProgramsCard = ({ coachId }) => {
    const routes = useCoachRoutes();
    const [which, setWhich] = useState('groups');
    const tabRefs = useRef({});

    const onKeyDown = (event, current) => {
        const index = TABS.indexOf(current);
        let next;
        if (event.key === 'ArrowRight') next = TABS[(index + 1) % TABS.length];
        else if (event.key === 'ArrowLeft') next = TABS[(index + TABS.length - 1) % TABS.length];
        if (!next) return;
        event.preventDefault();
        setWhich(next);
        tabRefs.current[next]?.focus();
    };

    return (
        <Card
            id="gp"
            module="coach-groups"
            title="Groups and programs"
            hideTitle
            flush
            right={
                <>
                    <div className={styles.seg} role="tablist" aria-label="Groups or programs">
                        {TABS.map(tab => (
                            <button
                                key={tab}
                                ref={el => (tabRefs.current[tab] = el)}
                                type="button"
                                role="tab"
                                aria-selected={which === tab}
                                tabIndex={which === tab ? 0 : -1}
                                onClick={() => setWhich(tab)}
                                onKeyDown={event => onKeyDown(event, tab)}
                            >
                                {LABEL[tab]}
                            </button>
                        ))}
                    </div>
                    <ActionLink to={routes[which]}>Manage</ActionLink>
                </>
            }
        >
            <div role="tabpanel">
                <List key={which} which={which} coachId={coachId} />
            </div>
        </Card>
    );
};

export default GroupsProgramsCard;
